import { createGlobalStyle, styled, keyframes } from 'styled-components'
import { Link } from 'react-router-dom'

export const ReferenceCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${({ theme }) => theme.colors.surface};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.3s ease;
  max-width: fit-content;
  margin: 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 4px;
    background: ${({ theme }) => theme.colors.primary};
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 8px 20px
      ${({ theme }) => (theme.mode === 'dark' ? 'rgba(99, 102, 241, 0.3)' : 'rgba(102, 126, 234, 0.2)')};

    &::before {
      transform: scaleY(1);
    }
  }

  &:active {
    transform: translateY(-1px);
  }
`

export const Container = styled.div`
  height: auto;
  min-height: calc(100vh - 52px);
  display: flex;
  padding: 20px;
  flex-direction: column;
  justify-content: start;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 720px) {
    height: 100%;
  }

  > div:not(:first-child) {
    margin-top: 20px;
  }
`

export const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
  padding: 20px;
  margin: 0 auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 1200px;
  width: 100%;

  h2 {
    margin: 20px 0px;
  }

  form {
    display: flex;
    flex-direction: column;
  }

  button {
    height: 40px;
  }
`

export const InputDiv = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
  margin-bottom: 10px;

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: start;
  }

  div {
    display: flex;
    flex: 2;
    gap: 10px;

    @media (max-width: 720px) {
      flex-direction: column;
      width: 100%;
      margin-top: 10px;
    }
  }

  span {
    flex: 1;
    min-width: 500px;
    font-size: 16px;

    @media (max-width: 1080px) {
      min-width: 250px;
    }

    @media (max-width: 720px) {
      min-width: auto;
      width: 100%;
    }
  }

  input {
    flex: 2;
    width: 100%;
    padding: 12px;
    font-size: 16px;
    border: 1px solid ${({ theme }) => theme.colors.inputBorder};
    border-radius: 6px;
    outline: none;
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) => theme.colors.inputBackground};
    transition: border-color 0.3s;

    @media (max-width: 720px) {
      margin-top: 10px;
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    -moz-appearance: textfield;

    &:focus {
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
`

export const ErrorMessage = styled.span`
  color: ${({ theme }) => theme.colors.danger};
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

export const Select = styled.select`
  width: 100%;
  box-sizing: border-box;
  flex: 1;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: 5px;
  background: ${({ theme }) => theme.colors.inputBackground};
  font-size: 16px;
  margin-left: 10px;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media (max-width: 720px) {
    margin-left: 0 !important;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

export const MathDiv = styled.div`
  display: flex !important;
  flex-direction: column !important;
  margin-left: 30px !important;
  align-items: baseline !important;

  .katex .sqrt > .sqrt-sign,
  .katex .sqrt svg {
    position: relative;
    top: -1em;
  }

  .katex .delimsizing.mult svg {
    position: relative;
    top: -1em;
    transform: scaleY(1.3);
  }

  @media (max-width: 720px) {
    margin-left: 0 !important;
  }
`

export const ButtonDiv = styled.div`
  display: flex;
  justify-content: center;

  button {
    background-color: ${({ $isFormValid, theme }) =>
      $isFormValid ? theme.colors.primary : theme.colors.surfaceElevated};
    color: ${({ $isFormValid, theme }) =>
      theme.mode === 'dark' ? '#ffffff' : $isFormValid ? theme.colors.onPrimary : theme.colors.textSecondary};
    border: 1px solid ${({ $isFormValid, theme }) => ($isFormValid ? 'transparent' : theme.colors.border)};
    padding: 14px 28px;
    width: 80%;
    max-width: 400px;
    min-height: 50px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 12px;
    margin-top: 40px;
    margin-bottom: 20px;
    cursor: ${({ $isFormValid }) => ($isFormValid ? 'pointer' : 'not-allowed')};
    box-shadow: ${({ $isFormValid }) => ($isFormValid ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none')};
    transition:
      background-color 0.3s ease,
      transform 0.2s ease,
      box-shadow 0.3s ease;

    &:hover {
      background-color: ${({ $isFormValid, theme }) =>
        $isFormValid ? theme.colors.primaryHover : theme.colors.surfaceMuted};
      transform: ${({ $isFormValid }) => ($isFormValid ? 'translateY(-2px) scale(1.02)' : 'none')};
      box-shadow: ${({ $isFormValid }) => ($isFormValid ? '0 6px 16px rgba(0, 0, 0, 0.15)' : 'none')};
    }

    &:active {
      background-color: ${({ $isFormValid, theme }) =>
        $isFormValid ? theme.colors.primaryActive : theme.colors.surfaceMuted};
      transform: ${({ $isFormValid }) => ($isFormValid ? 'translateY(0) scale(0.98)' : 'none')};
      box-shadow: ${({ $isFormValid }) => ($isFormValid ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none')};
    }

    @media (max-width: 720px) {
      width: 100%;
      max-width: 100%;
    }
  }
`

export const SaveAndLoadButtonsDiv = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 720px) {
    flex-direction: column;
    gap: 12px;
  }

  button {
    flex: 1;
    min-height: 50px;
    padding: 14px 24px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 12px;
    margin-top: 0;
    margin-bottom: 20px;
    cursor: pointer;
    border: none;
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : theme.colors.onPrimary)};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition:
      background-color 0.3s ease,
      transform 0.2s ease,
      box-shadow 0.3s ease;

    &:hover {
      background-color: ${({ theme }) => theme.colors.primaryHover};
      transform: translateY(-2px) scale(1.02);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    }

    &:active {
      background-color: ${({ theme }) => theme.colors.primaryActive};
      transform: translateY(0) scale(0.98);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    @media (max-width: 720px) {
      width: 100%;
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 20px;
      }
    }
  }
`

export const ActionButtonsRow = styled.div`
  display: flex;
  justify-content: center;
  //align-items: center;
  gap: 16px;
  margin: 40px 100px 20px;
  align-items: stretch;

  @media (max-width: 720px) {
    flex-direction: column;
    gap: 12px;
    margin: 30px auto 20px;
    width: 100%;
    max-width: 100%;
    align-items: stretch;
  }

  button {
    flex: 1;
    min-height: 50px;
    padding: 14px 24px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 12px;
    cursor: pointer;
    border: none;
    background-color: ${({ theme }) => (theme.mode === 'dark' ? '#6366f1' : theme.colors.primary)};
    color: #ffffff;
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark' ? '0 4px 16px rgba(99, 102, 241, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)'};
    transition:
      background-color 0.3s ease,
      transform 0.2s ease,
      box-shadow 0.3s ease;

    &:hover {
      background-color: ${({ theme }) => (theme.mode === 'dark' ? '#7c3aed' : theme.colors.primaryHover)};
      transform: translateY(-2px) scale(1.02);
      box-shadow: ${({ theme }) =>
        theme.mode === 'dark' ? '0 6px 20px rgba(124, 58, 237, 0.4)' : '0 6px 16px rgba(0, 0, 0, 0.15)'};
    }

    &:active {
      background-color: ${({ theme }) => (theme.mode === 'dark' ? '#5b21b6' : theme.colors.primaryActive)};
      transform: translateY(0) scale(0.98);
      box-shadow: ${({ theme }) =>
        theme.mode === 'dark' ? '0 2px 12px rgba(91, 33, 182, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
    }

    &:disabled {
      background-color: ${({ theme }) =>
        theme.mode === 'dark' ? theme.colors.surfaceMuted : theme.colors.surfaceElevated};
      color: ${({ theme }) => theme.colors.textSecondary};
      border: 2px solid ${({ theme }) => (theme.mode === 'dark' ? theme.colors.border : theme.colors.border)};
      cursor: not-allowed;
      box-shadow: none;
      opacity: ${({ theme }) => (theme.mode === 'dark' ? 0.7 : 0.6)};

      &:hover {
        background-color: ${({ theme }) =>
          theme.mode === 'dark' ? theme.colors.surfaceMuted : theme.colors.surfaceElevated};
        transform: none;
        box-shadow: none;
      }
    }

    @media (max-width: 720px) {
      width: 100%;
    }
  }
`

export const Result = styled.div`
  position: relative;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
  padding: 20px;
  margin: 0 auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 1200px;
  width: 100%;
  align-items: start;
  animation: ${(props) => (props.$isRecalculating ? 'none' : 'fadeInSlideUp 0.5s ease-out')};
  transition: all 0.3s ease;

  @keyframes fadeInSlideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  h4 {
    margin-top: 10px;
  }

  p {
    margin-top: 10px;
  }

  h3,
  svg {
    margin-top: 20px;
  }

  h2 {
    margin: 20px 0px 0 0;
  }
`

export const AlertDiv = styled.div`
  display: flex;
  align-items: center;

  svg {
    color: ${({ theme, success }) => (success ? theme.colors.success : theme.colors.danger)};
    width: 25px;
    height: 25px;
    margin-top: 0;
    margin-left: 10px;
    vertical-align: middle;
  }
`

export const DesktopOnlyDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  @media (max-width: 720px) {
    display: none !important;
  }
`

export const MobileOnlyDiv = styled.div`
  display: none !important;
  width: 100%;

  @media (max-width: 720px) {
    display: flex !important;
    flex-direction: column;
    align-items: flex-start;
  }
`

export const GlobalStyle = createGlobalStyle`
:root {
  color-scheme: ${({ theme }) => (theme.mode === 'dark' ? 'dark' : 'light')};
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body {
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.textPrimary};
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
}

#root {
  overflow-x: hidden;
  width: 100%;
  max-width: 100vw;
}

a {
  text-decoration: none;
  color: inherit;
}

html {
  @media (max-width: 1080px) {
    font-size: 93.75%;
  }

  @media (max-width: 720px) {
    font-size: 87.5%;
  }
}

body,
input,
textarea,
button {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
}

h1,
h2,
h3,
h4,
h5,
h6,
strong {
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
}

p {
  color: ${({ theme }) => theme.colors.textSecondary};
}

button {
  cursor: pointer;
}

[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}

.app-tooltip {
  --rt-opacity: 1;
  --rt-color-dark: ${({ theme }) => (theme.mode === 'dark' ? '#0f172a' : '#111827')};
  --rt-color-white: ${({ theme }) => (theme.mode === 'dark' ? '#e2e8f0' : '#f9fafb')};

  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f172a' : '#111827')} !important;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e2e8f0' : '#f9fafb')} !important;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#0f172a')} !important;
  border-radius: 10px !important;
  padding: 10px 12px !important;
  font-size: 12px !important;
  line-height: 1.4 !important;
  max-width: 240px !important;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark' ? '0 12px 24px rgba(15, 23, 42, 0.55)' : '0 10px 25px rgba(0, 0, 0, 0.25)'} !important;
}

.app-tooltip-arrow {
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f172a' : '#111827')} !important;
}

[class*='react-tooltip__place-'] {
  --rt-opacity: 1;
  --rt-color-dark: ${({ theme }) => (theme.mode === 'dark' ? '#0f172a' : '#111827')};
  --rt-color-white: ${({ theme }) => (theme.mode === 'dark' ? '#e2e8f0' : '#f9fafb')};

  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f172a' : '#111827')} !important;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e2e8f0' : '#f9fafb')} !important;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#0f172a')} !important;
  border-radius: 10px !important;
  padding: 10px 12px !important;
  font-size: 12px !important;
  line-height: 1.4 !important;
  max-width: 240px !important;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark' ? '0 12px 24px rgba(15, 23, 42, 0.55)' : '0 10px 25px rgba(0, 0, 0, 0.25)'} !important;
}

[class*='react-tooltip__place-'] > [class*='arrow'] {
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f172a' : '#111827')} !important;
}
`

export const CopyHTMLButton = styled.button`
  background-color: ${({ theme }) => (theme.mode === 'dark' ? '#6366f1' : theme.colors.primary)};
  color: #ffffff;
  padding: 10px 20px;
  min-height: 42px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  margin-top: 0;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark' ? '0 4px 16px rgba(99, 102, 241, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)'};
  transition:
    background-color 0.3s ease,
    transform 0.2s ease,
    box-shadow 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => (theme.mode === 'dark' ? '#7c3aed' : theme.colors.primaryHover)};
    transform: translateY(-2px) scale(1.02);
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark' ? '0 6px 20px rgba(124, 58, 237, 0.4)' : '0 6px 16px rgba(0, 0, 0, 0.15)'};
  }

  &:active {
    background-color: ${({ theme }) => (theme.mode === 'dark' ? '#5b21b6' : theme.colors.primaryActive)};
    transform: translateY(0) scale(0.98);
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark' ? '0 2px 12px rgba(91, 33, 182, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  }

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 13px;
    min-height: 38px;
  }
`

export const RadioInputDiv = styled(InputDiv)`
  height: auto;
  min-height: 56px;
  align-items: center;

  > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    row-gap: 12px;
  }

  @media (max-width: 720px) {
    min-height: auto;

    > div {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }
  }
`

export const CheckboxWrapper = styled.label`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: 12px;
  width: fit-content;
  min-height: 44px;

  input[type='checkbox'] {
    appearance: none;
    -webkit-appearance: none;
    width: 22px;
    height: 22px;
    border: 2px solid ${({ theme }) => theme.colors.inputBorder};
    border-radius: 6px;
    background: ${({ theme }) => theme.colors.inputBackground};
    cursor: pointer;
    position: relative;
    transition: all 0.3s ease;
    flex-shrink: 0;

    &:hover {
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
    }

    &:checked {
      background: ${({ theme }) => theme.colors.primary};
      border-color: ${({ theme }) => theme.colors.primary};

      &::after {
        content: '';
        position: absolute;
        left: 6px;
        top: 2px;
        width: 6px;
        height: 11px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    }

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .checkbox-label {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-size: 16px;
    line-height: 1.5;
  }
`

export const RadioWrapper = styled.label`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: 10px;
  flex: 0 1 160px;
  min-width: 120px;
  width: fit-content;

  @media (max-width: 720px) {
    flex: 1 1 100%;
    min-width: 0;
    gap: 8px;

    input[type='radio'] {
      width: 18px;
      height: 18px;
      min-width: 18px;
      min-height: 18px;
      aspect-ratio: 1 / 1;
    }

    span {
      line-height: 1.3;
      white-space: nowrap;
    }
  }

  input[type='radio'] {
    appearance: none;
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    min-width: 20px;
    min-height: 20px;
    aspect-ratio: 1 / 1;
    border: 2px solid ${({ theme }) => theme.colors.inputBorder};
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.inputBackground};
    cursor: pointer;
    position: relative;
    transition: all 0.3s ease;
    flex-shrink: 0;

    &:hover {
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
    }

    &:checked {
      border-color: ${({ theme }) => theme.colors.primary};
      background: ${({ theme }) => theme.colors.inputBackground};

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: ${({ theme }) => theme.colors.primary};
      }
    }

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  span {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-size: 16px;
    line-height: 1.5;
  }
`

export const CopyImageButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 20px;
  margin-top: 0;
  color: ${({ theme }) => theme.colors.textSecondary};

  @media (max-width: 720px) {
    margin-left: 0;
  }

  svg {
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-right: 5px;
    height: 20px;
    width: 20px;
  }
`

export const SvgImage = styled.svg`
  width: 500px;
  height: 500px;
  margin-top: 0;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#000000')};

  /* Harmonize strokes in dark mode without overriding dynamic text colors */
  path[stroke],
  line,
  polyline,
  polygon,
  rect[stroke],
  circle[stroke],
  ellipse[stroke] {
    stroke: currentColor;
  }

  path[fill='black'] {
    fill: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#000000')};
  }

  text[fill='black'],
  tspan[fill='black'] {
    fill: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#000000')};
  }

  /* Give white backgrounds a dark-friendly tone so drawings stay visible */
  rect[fill='black'] {
    fill: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#000000')};
  }

  ellipse[fill='black'] {
    fill: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#000000')};
  }

  rect[fill='white'] {
    fill: ${({ theme }) => (theme.mode === 'dark' ? theme.colors.surface : '#ffffff')};
  }

  @media (max-width: 768px) {
    width: 268px;
    height: 268px;
  }
`

export const ImageDiv = styled.div`
  display: flex;
  align-items: flex-start;

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: center;
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const PageContainer = styled.div`
  min-height: calc(100vh - 70px);
  animation: ${fadeIn} 0.6s ease-out;
`

export const PageHeader = styled.header`
  background: ${(props) => props.$gradient || props.theme.colors.headerGradient};
  color: white;
  padding: 40px 20px;
  margin-bottom: 40px;

  .header-content {
    max-width: 1200px;
    margin: 0 auto;

    .breadcrumb {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 20px;
      font-size: 0.9rem;
      opacity: 0.9;

      a {
        color: white;
        text-decoration: none;
        transition: opacity 0.3s ease;

        &:hover {
          opacity: 0.8;
        }
      }

      span {
        opacity: 0.6;
      }
    }

    h1 {
      font-size: 2.2rem;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      gap: 15px;
      color: white;

      @media (max-width: 720px) {
        font-size: 1.8rem;
      }
    }

    .subtitle {
      font-size: 1.1rem;
      opacity: 0.95;
      line-height: 1.5;
      max-width: 800px;
      color: white;

      @media (max-width: 720px) {
        font-size: 1rem;
      }
    }

    .badges {
      display: flex;
      gap: 10px;
      margin-top: 15px;
      flex-wrap: wrap;
    }
  }
`

export const Badge = styled.span`
  background: ${({ theme }) => theme.colors.badge};
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  backdrop-filter: blur(10px);
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

export const InfoCard = styled.div`
  max-width: 1200px;
  margin: 0 auto 30px;
  padding: 0 20px;

  .info-content {
    background: ${({ theme }) => (theme.mode === 'dark' ? theme.colors.surfaceElevated : theme.colors.surfaceElevated)};
    border: 1px solid ${({ theme }) => theme.colors.border};
    box-shadow: ${({ theme }) => theme.colors.cardShadow};
    border-radius: 12px;
    padding: 25px;
    display: flex;
    align-items: flex-start;
    gap: 20px;

    @media (max-width: 720px) {
      flex-direction: column;
    }

    .icon {
      font-size: 2.5rem;
      flex-shrink: 0;
    }

    .text {
      flex: 1;

      h3 {
        color: ${({ theme }) => theme.colors.textPrimary};
        font-size: 1.1rem;
        margin-bottom: 8px;
      }

      p {
        color: ${({ theme }) => theme.colors.textSecondary};
        line-height: 1.6;
        margin-bottom: 10px;
      }

      .norms {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        margin-top: 12px;

        span {
          background: ${({ theme }) => (theme.mode === 'dark' ? theme.colors.surface : theme.colors.surfaceElevated)};
          border: 1px solid ${({ theme }) => theme.colors.border};
          color: ${({ theme }) => theme.colors.primary};
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
        }
      }
    }
  }
`

export const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: white;
  text-decoration: none;
  font-size: 0.95rem;
  padding: 8px 16px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  margin-bottom: 15px;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateX(-3px);
  }
`

export const AdvancedOptionsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)'
      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)'};
  border: 2px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(99, 102, 241, 0.3)' : 'rgba(102, 126, 234, 0.25)')};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#818cf8' : '#667eea')};
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  margin-top: 30px;
  margin-bottom: 20px;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark' ? '0 4px 12px rgba(99, 102, 241, 0.15)' : '0 2px 8px rgba(102, 126, 234, 0.1)'};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)'
        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)'};
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(99, 102, 241, 0.5)' : 'rgba(102, 126, 234, 0.4)')};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark' ? '0 6px 20px rgba(99, 102, 241, 0.25)' : '0 4px 16px rgba(102, 126, 234, 0.2)'};

    &::before {
      opacity: 1;
    }

    .arrow-icon {
      transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg) translateY(2px)' : 'translateX(3px)')};
    }
  }

  &:active {
    transform: translateY(0);
  }

  .arrow-icon {
    display: inline-flex;
    align-items: center;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    font-size: 12px;
  }

  .button-text {
    position: relative;
    z-index: 1;
  }
`

// Index page components
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const scaleIn = keyframes`
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`

export const IndexContainer = styled.main`
  min-height: calc(100vh - 52px);
  animation: ${fadeIn} 0.6s ease-out;

  .content-wrapper {
    max-width: 1200px;
    margin: 0 auto;
    padding: 60px 20px 80px;

    h2 {
      font-size: 2rem;
      margin-bottom: 30px;
      color: ${({ theme }) => theme.colors.textPrimary};
      text-align: center;
      animation: ${fadeInUp} 0.8s ease-out;
    }
  }
`

export const IndexHeader = styled.header`
  background: ${(props) => props.$gradient || props.theme.colors.heroGradient};
  color: white;
  padding: 60px 20px;
  margin-bottom: 0;
  animation: ${fadeIn} 0.8s ease-out;

  .header-content {
    max-width: 1200px;
    margin: 0 auto;
    text-align: center;
    animation: ${fadeInUp} 0.8s ease-out 0.2s both;

    h1 {
      font-size: 2.5rem;
      margin-bottom: 15px;
      line-height: 1.2;
      color: white;

      @media (max-width: 720px) {
        font-size: 2rem;
      }
    }

    .subtitle {
      font-size: 1.2rem;
      margin-bottom: 30px;
      opacity: 0.92;
      color: white;
      max-width: 800px;
      margin-left: auto;
      margin-right: auto;
      line-height: 1.6;

      @media (max-width: 720px) {
        font-size: 1rem;
      }
    }

    .stats {
      display: flex;
      justify-content: center;
      gap: 40px;
      margin-top: 30px;

      @media (max-width: 720px) {
        gap: 20px;
      }

      .stat-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 20px 30px;
        background: rgba(255, 255, 255, 0.15);
        border-radius: 12px;
        backdrop-filter: blur(10px);
        transition: all 0.3s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-3px);
        }

        @media (max-width: 720px) {
          padding: 15px 20px;
        }

        .stat-number {
          font-size: 2rem;
          font-weight: bold;
          margin-bottom: 5px;

          @media (max-width: 720px) {
            font-size: 1.5rem;
          }
        }

        .stat-label {
          font-size: 0.9rem;
          opacity: 0.9;

          @media (max-width: 720px) {
            font-size: 0.8rem;
          }
        }
      }
    }
  }
`

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 25px;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

export const IndexCard = styled(Link)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 12px;
  padding: 30px;
  text-decoration: none;
  color: inherit;
  border: 2px solid
    ${(props) => (props.$popular ? props.$accentColor || props.theme.colors.primary : props.theme.colors.border)};
  position: relative;
  transition: all 0.3s ease;
  animation: ${scaleIn} 0.6s ease-out;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    border-color: ${(props) => props.$accentColor || props.theme.colors.primary};
    box-shadow: ${(props) =>
      props.$accentColor ? `0 10px 30px ${props.$accentColor}33` : `0 10px 30px ${props.theme.colors.primary}33`};

    .card-icon {
      transform: scale(1.2) rotate(5deg);
    }

    .arrow {
      transform: translateX(5px);
    }
  }

  .popular-badge {
    position: absolute;
    top: -12px;
    right: 20px;
    background: ${(props) => props.$gradient || props.theme.colors.heroGradient};
    color: white;
    padding: 5px 15px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    box-shadow: ${(props) =>
      props.$accentColor ? `0 4px 10px ${props.$accentColor}4d` : '0 4px 10px rgba(102, 126, 234, 0.3)'};
  }

  .upcoming-badge {
    position: absolute;
    top: -12px;
    right: 20px;
    background: ${({ theme }) => theme.colors.surfaceMuted};
    color: ${({ theme }) => theme.colors.textPrimary};
    padding: 5px 15px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    border: 1px solid ${({ theme }) => theme.colors.border};
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  }

  .card-icon {
    font-size: 3rem;
    margin-bottom: 15px;
    transition: transform 0.3s ease;
  }

  h3 {
    font-size: 1.4rem;
    margin-bottom: 10px;
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: 600;
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1.6;
    margin-bottom: 20px;
    flex: 1;
  }

  .arrow {
    align-self: flex-end;
    font-size: 1.5rem;
    color: ${(props) => props.$accentColor || props.theme.colors.primary};
    transition: transform 0.3s ease;
  }
`
